import { useQuery } from 'react-query'
import { makeCoreGetRequest } from 'utils/api'
import { handleQueryError } from 'utils/helpers'
import {
  SimpleItem,
  LocationSubTypeItem,
  LocationAddressItem,
  RegionResponseItem
} from './interfaces'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'

export const getLegalEntititesOptions = (showNotification: Function) => {
  return useQuery(
    ['legal-entities'],
    async () => {
      const data = await makeCoreGetRequest('/location-management/legal-entities')
      return data
        .filter(item => item.active)
        .map((item: SimpleItem) => ({
          label: item.name,
          value: item.id
        }))
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching legal entities.', showNotification)
    }
  )
}

export const getLocationStatusesOptions = (showNotification: Function) => {
  return useQuery(
    ['location-statuses'],
    async () => {
      const data = await makeCoreGetRequest('/location-management/location-statuses')
      return data.map((item: SimpleItem) => ({
        label: item.name,
        value: item.id
      }))
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location statuses.', showNotification)
    }
  )
}

export const getLocationTypesOptions = (showNotification: Function) => {
  return useQuery(
    ['location-types'],
    async () => {
      const data = await makeCoreGetRequest('/location-management/location-types')
      return data.map((item: SimpleItem) => ({
        label: item.name,
        value: item.id
      }))
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location types.', showNotification)
    }
  )
}

export const getLocationSubtypesOptions = (showNotification: Function, typeId: number) => {
  return useQuery(
    ['location-subtypes', typeId],
    async () => {
      const data = await makeCoreGetRequest(`/location-management/location-subtypes/${typeId}`)
      return data.map((item: LocationSubTypeItem) => ({
        label: `${item.name}${item.businessType ? ` ${item.businessType.name}` : ''}`,
        value: item.id
      }))
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location subtypes.', showNotification),
      enabled: !!typeId
    }
  )
}

export const getLocationRegionsOptions = (showNotification: Function) => {
  return useQuery(
    ['location-regions'],
    async () => {
      const data = await makeCoreGetRequest('/location-management/region-with-category')
      return data.map((item: RegionResponseItem) => ({
        label: `${item.category} - ${item.name}`,
        value: item.id,
        groupBy: item.category
      }))
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location subtypes.', showNotification)
    }
  )
}

export const getLocationSystemsOptions = (showNotification: Function) => {
  return useQuery(
    ['location-systems-options'],
    async () => {
      const data = await makeCoreGetRequest('/location-management/location-systems')
      return data.map((item: SimpleItem) => ({
        label: item.name,
        value: item.name
      }))
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location systems.', showNotification)
    }
  )
}

export const getLocationSystems = (showNotification: Function) => {
  return useQuery(
    ['location-systems'],
    async () => {
      const data = await makeCoreGetRequest('/location-management/location-systems')
      return data
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location systems.', showNotification)
    }
  )
}

export const getExistingAddresses = (showNotification: Function) => {
  return useQuery(
    ['existing-addresses'],
    async () => {
      const data = await makeCoreGetRequest('/location-management/location-usage-addresses')
      return data.map((item: LocationAddressItem) => ({
        label: item.name,
        value: item.id,
        completeAddress: item.completeAddress
      }))
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching existing addresses.', showNotification)
    }
  )
}

export const getLocation = (
  showNotification: Function,
  locationId: number,
  shouldRefreshData?: boolean,
  setShouldRefreshData?: Function
) => {
  return useQuery(
    ['location-item', locationId, shouldRefreshData],
    async () => {
      if (!locationId) return null

      const result = await makeCoreGetRequest(`/location-management/location-usage/${locationId}`)
      setShouldRefreshData && setShouldRefreshData(false)
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location.', showNotification)
    }
  )
}

export const getLocationUnit = (
  showNotification: Function,
  locationUnitId: number,
  shouldRefreshData: boolean
) => {
  return useQuery(
    ['location-unit-item', locationUnitId, shouldRefreshData],
    async () => {
      if (!locationUnitId) return null

      const result = await makeCoreGetRequest(
        `/location-management/location-unit/${locationUnitId}`
      )
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location.', showNotification)
    }
  )
}
